import { CardElement } from "@stripe/react-stripe-js";
import { UseMutationResult } from "@tanstack/react-query";
import useDarkMode from "use-dark-mode";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

import { PaymentMethod } from "data/models";
import { ReactQueryMutationError } from "data/mutations-common";

interface EditPaymentMethodDialogueProps {
  open: boolean;
  handleOnClose: () => void;
  stripeUrl: string;
  paymentMethodMutation: UseMutationResult<
    PaymentMethod,
    ReactQueryMutationError,
    void,
    unknown
  >;
}

export const EditPaymentMethodDialogue = ({
  open,
  handleOnClose,
  stripeUrl,
  paymentMethodMutation,
}: EditPaymentMethodDialogueProps) => {
  const isPending = paymentMethodMutation.isPending;
  const { value: isDark } = useDarkMode(false, { classNameDark: "dark" });

  const cardElementStyle = {
    base: {
      color: isDark ? "#f5f5f5" : "#111111",
      fontFamily: "inherit",
      fontSize: "14px",
      "::placeholder": {
        color: isDark ? "#8a8a8a" : "#6b7280",
      },
      iconColor: isDark ? "#f5f5f5" : "#111111",
    },
    invalid: {
      color: "#ef4444",
      iconColor: "#ef4444",
    },
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (isPending) return;
        if (!nextOpen) handleOnClose();
      }}
    >
      <DialogContent
        className="sm:max-w-112.5 bg-card"
        onEscapeKeyDown={(e) => {
          if (isPending) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isPending) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (isPending) e.preventDefault();
        }}
        showCloseButton={!isPending}
      >
        <DialogHeader>
          <DialogTitle>Update Credit Card</DialogTitle>
          <DialogDescription>
            Payments are processed securely through{" "}
            <a
              className="underline"
              href={stripeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Stripe
            </a>
            .
          </DialogDescription>
        </DialogHeader>

        <div className="border border-input bg-background rounded-md p-3">
          <CardElement
            options={{
              hidePostalCode: true,
              style: cardElementStyle,
            }}
          />
        </div>

        {paymentMethodMutation.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {paymentMethodMutation.error.message}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            disabled={isPending}
            onClick={() => handleOnClose()}
          >
            Cancel
          </Button>
          <Button
            data-cy="save-changes"
            disabled={isPending}
            onClick={() => paymentMethodMutation.mutate()}
          >
            {isPending ? (
              <>
                <Spinner />
                Save Changes
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
